import { ChangeDetectionStrategy, Component, computed, effect, inject, input, InputSignal, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { RouteDataService } from '../../services/route-data.service';
import { Point, ItemStatus } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { DEFAULT_MARKER, SATURATED_MARKER, RECOMMENDED_MARKER } from './config/constants';
import { WebsocketLaravelReverb } from 'src/app/fitur-26/pages/home/services/websocket-laravel-reverb';
import { Router } from '@angular/router';
import { CHANNEL_ATRIL_PANTALLA_NAME, GO_TO_NORMAL_OR_SATURED_DETAIL_PAGE_EVENT } from 'src/app/fitur-26/pages/home/config/constants';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';

@Component({
    selector: 'fitur-map-detail-page-markers',
    standalone: true,
    imports: [ 
        CommonModule, 
        GoogleMapsModule,
    ],
    templateUrl: './detail-page-marker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class FiturMapDetailPageMarkerComponent {
    public map: InputSignal<any> = input<any>();
    public detailPoint: InputSignal<any> = input<any>(undefined);
    private _routeDataService: RouteDataService = inject(RouteDataService);
    private _customRouteService = inject(CustomRouteService);
    private _websocketLaravelReverbService = inject(WebsocketLaravelReverb);
    private _router = inject(Router);

    private _routeToken =  this._customRouteService.token;
    private _zoomIntervalId: ReturnType<typeof setInterval> | null = null;
    public routeSegments = computed<Point[]>(() =>this._routeDataService.getCurrentRouteDay())

    public detailsItem = computed(() => this.routeItem() ? undefined : this.detailPoint());
    public routeItem = computed(() => this.routeSegments()?.find(e => e.id === this.detailPoint()?.id))
    public routeItemIndex = computed(() => this.routeSegments()?.findIndex(e => e.id === this.detailPoint()?.id))

    checkRouteItem = effect(() => {
        const location = this.routeItem()?.currentLocation;
        if (!location)
            return;

        console.log("DETAILS: checkRouteItem", this.routeItem())
        const suggestedItems = this.routeItem()?.relatedsItems?.map?.(point => point.currentLocation) ?? undefined;
        const coords = [location, suggestedItems]?.flat()?.filter(e => e !== undefined);
        if (coords.length > 1){
            this._fitBounds(coords)
            return;
        }
        
        this._zoomToCoordinates(location);
    })

    checkDetailItem = effect(() => {
        const location = this.detailsItem()?.location;
        if (!location)
            return;

        console.log("DETAILS: checkDetailItem", this.detailsItem())
        this._zoomToCoordinates(location);
    })


    // Markers --------------------------------------------------------------------------------------------------------------------
    public defaultMakerOption = (index) => DEFAULT_MARKER(index + 1);
    public saturatedMakerOption = (index) => SATURATED_MARKER(index + 1);
    public recommendedMarkerOption = (index?) => RECOMMENDED_MARKER(index);


    public handleRecommendedMarkerClick(item: Point): void{
        if (!item?.id)
            return;

        const typeNormalized = item.type !== 'event' ? 'point' : 'event';
        this._websocketLaravelReverbService
            .sendMessage(
                CHANNEL_ATRIL_PANTALLA_NAME,
                GO_TO_NORMAL_OR_SATURED_DETAIL_PAGE_EVENT+ this._routeToken(),
                { type: typeNormalized, id: item.id }
            )
        
        this._router.navigate(
            ['/home', typeNormalized, item.id],
            {
                queryParams: {
                    from: 'saturated',
                    pointId: this.routeItem()?.id,
                    pointType: this.routeItem()?.type
                }
            }
        );
    }

    public getItemOptions(item){
        return item.relateds
            ? this.saturatedMakerOption(this.routeItemIndex())
            : this.defaultMakerOption(this.routeItemIndex())
    }

    private _fitBounds(coords){
        setTimeout(() => {
            const bounds = new google.maps.LatLngBounds();
            coords?.forEach(coord => bounds.extend(new google.maps.LatLng(coord)));
            this.map()?.googleMap?.fitBounds(bounds);
        }, 200);

    }

    private _zoomToCoordinates(coords: { lat: number; lng: number }): void {
        if (!this.map()?.googleMap) 
            return;
        
        const targetZoom = 18; // Zoom final deseado
        const currentZoom = this.map().googleMap.getZoom() || 10;
        
        // Primero centrar en las coordenadas
        this.map().googleMap.panTo(coords);
        
        // Animar el zoom gradualmente
        this._animateZoom(currentZoom, targetZoom);
    }

    private _animateZoom(currentZoom: number, targetZoom: number): void {
        if (!this.map()?.googleMap) 
            return;
        
        // Limpiar animación anterior si existe
        if (this._zoomIntervalId) {
            clearInterval(this._zoomIntervalId);
        }
        
        const step = currentZoom < targetZoom ? 0.5 : -0.5;
        const duration = 50;
        
        this._zoomIntervalId = setInterval(() => {
            currentZoom += step;
            
            if ((step > 0 && currentZoom >= targetZoom) || (step < 0 && currentZoom <= targetZoom)) {
                this.map()?.googleMap?.setZoom(targetZoom);
                clearInterval(this._zoomIntervalId!);
                this._zoomIntervalId = null;
            } else {
                this.map()?.googleMap?.setZoom(currentZoom);
            }
        }, duration);
    }

    ngOnDestroy(): void {
        console.log("Destroying DetailPageMarker!")
        if (this._zoomIntervalId) {
            clearInterval(this._zoomIntervalId);
            this._zoomIntervalId = null;
        }
    }
    
}